import axios from 'axios';
import * as Constants from './constants';
import authHeader from './auth-header';

const SETTINGS_KEY =
  'indexcasePrivacySettings';

const CONSENT_KEY =
  'indexcaseConsentGrants';

export const DEFAULT_PRIVACY_SETTINGS = {
  automaticLocation: false,
  shareExposureStays: false,
  exposureNotifications: true,
  hotspotWarnings: true,
  symptomCorrelation: true,
  retainRawLocationHours: 24,
};

export function getPrivacySettings() {
  try {
    return {
      ...DEFAULT_PRIVACY_SETTINGS,
      ...(
        JSON.parse(
          localStorage.getItem(
            SETTINGS_KEY
          )
        ) || {}
      )
    };
  } catch (_) {
    return {
      ...DEFAULT_PRIVACY_SETTINGS
    };
  }
}

export function savePrivacySettings(
  settings
) {
  const next = {
    ...DEFAULT_PRIVACY_SETTINGS,
    ...settings
  };

  localStorage.setItem(
    SETTINGS_KEY,
    JSON.stringify(next)
  );

  return next;
}

function parseArray(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return [];
  }

  try {
    const parsed =
      JSON.parse(value);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch (_) {
    return [];
  }
}

function cachedConsents() {
  try {
    const value =
      JSON.parse(
        localStorage.getItem(
          CONSENT_KEY
        )
      );

    return Array.isArray(value)
      ? value
      : [];
  } catch (_) {
    return [];
  }
}

function normalizeConsent(
  row,
  oldRows = []
) {
  const providerId =
    Number(
      row.providerId ??
      row.provider_id
    );

  const previous =
    oldRows.find(
      item =>
        Number(item.id) ===
          Number(row.id) ||
        (
          Number(item.providerId) ===
            providerId &&
          Number.isFinite(providerId)
        )
    );

  return {
    id:
      Number(row.id),
    providerId,
    providerName:
      row.providerName ||
      (
        previous &&
        previous.providerName
      ) ||
      (
        Number.isFinite(providerId)
          ? `Provider account ${providerId}`
          : 'Authorized provider'
      ),
    scopes:
      parseArray(row.scopes),
    episodeIds:
      row.episodeIds !==
        undefined
        ? parseArray(
            row.episodeIds
          )
        : parseArray(
            row.episode_ids
          ),
    createdAt:
      row.createdAt ??
      row.created_at ??
      null,
    expiresAt:
      row.expiresAt ??
      row.expires_at ??
      null,
    revokedAt:
      row.revokedAt ??
      row.revoked_at ??
      null,
    serverBacked: true
  };
}

function storeConsentGrants(
  rows
) {
  const oldRows =
    cachedConsents();

  const normalized =
    (rows || []).map(
      row =>
        normalizeConsent(
          row,
          oldRows
        )
    );

  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify(normalized)
  );

  return normalized;
}

export function getConsentGrants() {
  return cachedConsents();
}

export async function refreshConsentGrants() {
  const headers =
    authHeader();

  if (!headers.Authorization) {
    return getConsentGrants();
  }

  const response =
    await axios.get(
      Constants.API_URL +
        'consents',
      { headers }
    );

  if (
    !response ||
    !Array.isArray(
      response.data
    )
  ) {
    throw new Error(
      'Consent response was invalid.'
    );
  }

  return storeConsentGrants(
    response.data
  );
}

export async function saveConsentGrant(
  grant
) {
  const headers =
    authHeader();

  if (!headers.Authorization) {
    throw new Error(
      'Sign in before granting provider access.'
    );
  }

  const response =
    await axios.post(
      Constants.API_URL +
        'consents',
      {
        providerId:
          grant.providerId,
        scopes:
          grant.scopes,
        episodeIds:
          grant.episodeIds ||
          undefined,
        expiresAt:
          grant.expiresAt ||
          undefined
      },
      { headers }
    );

  if (
    !response ||
    response.status !== 201
  ) {
    throw new Error(
      'Provider access could not be created.'
    );
  }

  return refreshConsentGrants();
}

export async function revokeConsentGrant(
  consentId
) {
  const headers =
    authHeader();

  if (!headers.Authorization) {
    throw new Error(
      'Sign in before revoking provider access.'
    );
  }

  await axios.delete(
    Constants.API_URL +
      `consents/${consentId}`,
    { headers }
  );

  return refreshConsentGrants();
}

export const CONSENT_SCOPES = [
  {
    id: 'symptoms',
    label:
      'Symptoms and symptom history'
  },
  {
    id: 'diseaseEvidence',
    label:
      'Disease and verification evidence'
  },
  {
    id: 'exposures',
    label:
      'Possible exposure history'
  },
  {
    id: 'relevantLocations',
    label:
      'Locations relevant to the health episode'
  },
];
